import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import * as apiGet from 'api/identityProvider/GetIdentityProviderIdV1';
import IdentityProvider from 'classes/StandingData/IdentityProvider/IdentityProvider';
import BtnSave from 'components/desktop/_general/Button/BtnSave/BtnSave';
import LoadingAnimation from 'components/desktop/_general/Loading/LoadingAnimation';
import PictureUpload from 'components/desktop/_general/PictureUpload/PictureUpload';
import Toggle from 'components/desktop/_general/Toggle/Toggle';
import { MEDIACOLLECTION } from 'enums/mediaCollection';
import clone from 'functions/clone';
import { uploadUserMedia } from 'functions/uploadMedia';
import { getIdFromUrl, setIdToUrl } from 'functions/url';

const IdentityProviderDetailComponent = () => {
	const { t } = useTranslation();

	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isLoadingSave, setIsLoadingSave] = useState<boolean>(false);

	const [identityProvider, setIdentityProvider] = useState<IdentityProvider>(
		new IdentityProvider()
	);
	const [id, setId] = useState<string | null>(null);
	const [iconId, setIconId] = useState<string | null>(null);

	useEffect(() => {
		initialize();
	}, []);

	const initialize = async (): Promise<void> => {
		const id: string | null = getIdFromUrl();
		setId(id);
		if (id !== null && id !== 'new') {
			getIdentityProvider(id);
		}
	};

	const getIdentityProvider = async (id: string): Promise<void> => {
		setIsLoading(true);
		const response = await apiGet.callApi(id);
		response.do((x) => setIdentityProvider(new IdentityProvider(x)));
		setIsLoading(false);
	};

	const handleInputValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const name = e.currentTarget.name;
		switch (name) {
			case 'name':
				identityProvider.name = e.currentTarget.value;
				break;
			case 'clientId':
				identityProvider.clientId = e.currentTarget.value;
				break;
			case 'clientSecret':
				identityProvider.clientSecret = e.currentTarget.value;
				break;
			case 'openIdConnectDiscoveryUrl':
				identityProvider.openIdConnectDiscoveryUrl = e.currentTarget.value;
				break;
			case 'sortOrder':
				identityProvider.sortOrder = Number(e.currentTarget.value);
				break;
		}
		setIdentityProvider(clone(identityProvider));
	};

	const handleSaveIdentityProvider = async (): Promise<void> => {
		setIsLoadingSave(true);
		try {
			if (id !== null && id !== 'new') {
				await identityProvider.callApiPut(iconId);
			} else {
				await identityProvider.callApiPost(iconId);
				if (!identityProvider.id) return;
				setId(identityProvider.id);
				setIdToUrl(identityProvider.id);
			}
		} catch {
			setIsLoadingSave(false);
		} finally {
			setIsLoadingSave(false);
		}
	};

	const createHandleToggle = (inputId: string): ((input: boolean) => void) => {
		return (input: boolean): void => {
			switch (inputId) {
				case 'clientAuthenticationRequired':
					identityProvider.clientAuthenticationRequired = input;
					break;
				case 'propagateLogout':
					identityProvider.propagateLogout = input;
					break;
				case 'useRefreshTokenForLogout':
					identityProvider.useRefreshTokenForLogout = input;
					break;
			}
			setIdentityProvider(clone(identityProvider));
		};
	};

	const handleIconUpload = async (input: FormData | null) => {
		const res = await uploadUserMedia(input, MEDIACOLLECTION.Icon);
		res.do((media) => setIconId(media.id));
	};

	const renderContent = () => {
		return (
			<>
				<LoadingAnimation isLoading={isLoading} />
				<div className="row">
					<div className="d-flex flex-row justify-content-between mb-3">
						<div>
							{identityProvider.id ? (
								<>
									<h1>{t('standingData:EDIT_IDENTITY_PROVIDER')}</h1>
								</>
							) : (
								<>
									<h1>{t('standingData:NEW_IDENTITY_PROVIDER')}</h1>
								</>
							)}
						</div>
						<div>
							<BtnSave
								handleSave={handleSaveIdentityProvider}
								isLoading={isLoadingSave}
							/>
						</div>
					</div>
				</div>
				<div className="row">
					<div className="col-md-6 col-xl-6 col-lg-6 col-sm-6 col-12">
						<PictureUpload setFormData={handleIconUpload} />
						<Toggle
							checked={identityProvider.clientAuthenticationRequired}
							handleToggle={createHandleToggle('clientAuthenticationRequired')}
							elementId="clientAuthenticationRequired"
							label={'standingData:CLIENT_AUTHENTICATION_REQUIRED'}
							errorKey="clientAuthenticationRequired"
						/>
						<Toggle
							checked={identityProvider.propagateLogout}
							handleToggle={createHandleToggle('propagateLogout')}
							elementId="propagateLogout"
							label={'standingData:PROPAGATE_LOGOUT'}
							errorKey="propagateLogout"
						/>
						<Toggle
							checked={identityProvider.useRefreshTokenForLogout}
							handleToggle={createHandleToggle('useRefreshTokenForLogout')}
							elementId="useRefreshTokenForLogout"
							label={'standingData:USE_REFRESH_TOKEN_FOR_LOGOUT'}
							errorKey="useRefreshTokenForLogout"
						/>
					</div>
					<div className="col-md-6 col-xl-6 col-lg-6 col-sm-6 col-12">
						<label>{t('standingData:NAME')}</label>
						<div>
							<input
								value={identityProvider.name}
								type="text"
								onChange={handleInputValueChange}
								className="form-control"
								name="name"
								error-key={'name'}
							/>
						</div>
						<label>{t('standingData:CLIENT_ID')}</label>
						<div>
							<input
								value={identityProvider.clientId}
								type="text"
								onChange={handleInputValueChange}
								className="form-control"
								name="clientId"
								error-key={'clientId'}
							/>
						</div>
						<label>{t('standingData:CLIENT_SECRET')}</label>
						<div>
							<input
								value={identityProvider.clientSecret}
								type="text"
								onChange={handleInputValueChange}
								className="form-control"
								name="clientSecret"
								error-key={'clientSecret'}
							/>
						</div>
						<label>{t('standingData:OPEN_ID_CONNECT_DISCOVERY_URL')}</label>
						<div>
							<input
								value={identityProvider.openIdConnectDiscoveryUrl}
								type="text"
								onChange={handleInputValueChange}
								className="form-control"
								name="openIdConnectDiscoveryUrl"
								error-key={'openIdConnectDiscoveryUrl'}
							/>
						</div>
						<label>{t('standingData:SORT_ORDER')}</label>
						<div>
							<input
								value={identityProvider.sortOrder}
								type="number"
								onChange={handleInputValueChange}
								className="form-control"
								name="sortOrder"
								error-key={'sortOrder'}
							/>
						</div>
					</div>
				</div>
			</>
		);
	};

	return <>{renderContent()}</>;
};

export default IdentityProviderDetailComponent;
